import React from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { useMeasure } from 'react-use'

// assets
import { Envelope, Favorite, LinkIcon, Location, Supporters } from '../Icons/Icons'
import { ReactComponent as DefaultLogo } from 'assets/images/icon-group.svg'
import { ReactComponent as VerifiedIcon } from 'assets/images/icons/verified-org.svg'

// config
import constants from 'config/constants'
import l, { onlyBp, isGs } from 'config/localization'

// utils
import stringUtils from 'utils/string'
import th from 'utils/themeHelper'

// Load components synchronously
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import InfoField from 'components/shared/InfoField'
import LinkButton from 'components/SmallButtonPrimary/LinkButton'
import SingleCard from 'components/SingleCard/SingleCard'
import SmallButtonPrimary from 'components/SmallButtonPrimary/SmallButtonPrimary'

const deriveMargin = ({ first, last, width }) => {
  if (width > 350) {
    return first ? '0 5px 10px 0' : last ? '0 0 10px 5px' : '0 5px 10px'
  }
  return '0 0 10px 0'
}

const Heading = styled.h4`
  display: inline-block;
  margin: 0;
  vertical-align: middle;
`

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Actions = styled.div`
  align-items: ${props => (props.width > 350 ? 'flex-end' : 'center')};
  display: flex;
  flex: 1;
  flex-direction: ${props => (props.width > 350 ? 'row' : 'column')};
  justify-content: flex-end;
  margin: 0 15px;
`

const ActionLink = styled(Link)`
  margin: ${props => deriveMargin(props)};
  text-decoration: none;
  text-align: center;
  white-space: nowrap;
  width: 100%;
`

const Info = styled.div`
  display: block;
  margin: 10px 15px;
  flex: 1;
`

const InfoGroup = styled.div`
  min-width: 30%;
`

const InfoGroupRow = styled.div`
  color: ${th('text.mid')};
  line-height: 1.25em;
  margin-bottom: 15px;
  max-width: 350px;
  word-break: break-word;
`

const InactiveOrgLogo = styled.div`
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0.65) 100%),
    url(${({ src }) => src});
  background-position: center center;
  background-size: cover;
  height: 150px;
  width: 100%;
`

const Logo = styled.div`
  background-image: url(${({ src }) => src});
  background-size: cover;
  background-position: center center;
  border-radius: 4px 4px 0 0;
  width: 100%;
  height: 150px;
`

const OrgDescription = styled.div`
  display: -webkit-box;
  font-size: 12px;
  line-height: 1.35;
  margin: 10px 15px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`

const HorizontalLayout = styled.div`
  display: block;
  line-height: 1.25em;
  width: 100%;
`

const TitleWrapper = styled.div`
  margin: 15px 15px 0 15px;
`

const VerificationWrapper = styled.div`
  display: inline-block;
  margin-right: 5px;
  vertical-align: middle;
`

const VerificationIcon = styled.img`
  display: inline-block;
  width: 25px;
  height: 25px;
  vertical-align: middle;
`

const InactiveOrg = styled.div`
  background: ${th('primary.dark')};
  color: white;
  right: 10px;
  top: 10px;
  padding: 8px 16px;
  position: absolute;
`

const OrganisationCard = ({
  id,
  name,
  description,
  url_name,
  url,
  logo_image,
  org_logo_image,
  status,
  org_type_name,
  can_be_claimed,
  location,
  org_primary_email,
  superAdminIds,
  userData,
  hasGodAdminRole,
  is_verified,
  prefCategory
}) => {
  const canEdit = hasGodAdminRole
    ? true
    : superAdminIds && superAdminIds.length
    ? superAdminIds.includes(userData.id)
    : false

  const visibleUrl = url && url.length > 50 ? `${url.slice(0, 40)}...` : url ? url : null
  const isInactive = status === constants.ORG_STATUSES.INACTIVE
  const preferredCategoryName = prefCategory ? prefCategory.name : null
  const shortOrgType = stringUtils.shortVersionOf(org_type_name)
  const logoImage = logo_image || org_logo_image
  const canBeClaimed = hasGodAdminRole ? false : can_be_claimed
  const [ref, { width }] = useMeasure()

  return (
    <SingleCard fullHeight>
      <ActionsWrapper ref={ref}>
        <ConditionalDisplay displayWhen={[!isInactive]}>
          <Logo src={stringUtils.isBlank(logoImage) ? DefaultLogo : logoImage} />
        </ConditionalDisplay>

        <ConditionalDisplay displayWhen={[isInactive]}>
          <InactiveOrgLogo src={stringUtils.isBlank(logoImage) ? DefaultLogo : logoImage} />
          <InactiveOrg>Inactive {l('ORG')}</InactiveOrg>
        </ConditionalDisplay>

        <HorizontalLayout>
          <TitleWrapper>
            <ConditionalDisplay displayWhen={[is_verified]}>
              <VerificationWrapper>
                <VerificationIcon src={VerifiedIcon} />
              </VerificationWrapper>
            </ConditionalDisplay>

            <Heading>{name}</Heading>
          </TitleWrapper>

          <OrgDescription>{description}</OrgDescription>
        </HorizontalLayout>

        <Info>
          <InfoGroup>
            <InfoGroupRow>
              <InfoField icon={Supporters}>{shortOrgType}</InfoField>
            </InfoGroupRow>

            <ConditionalDisplay displayWhen={[prefCategory]}>
              <InfoGroupRow>
                <InfoField icon={Favorite}>{preferredCategoryName}</InfoField>
              </InfoGroupRow>
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[onlyBp]}>
              <InfoGroupRow>
                <InfoField icon={Location}>{location || 'Location not specified'}</InfoField>
              </InfoGroupRow>

              <InfoGroupRow>
                <InfoField icon={Envelope}>{org_primary_email || 'N/A'}</InfoField>
              </InfoGroupRow>
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[visibleUrl]}>
              <InfoField breakAnywhere icon={LinkIcon}>
                {visibleUrl}
              </InfoField>
            </ConditionalDisplay>
          </InfoGroup>
        </Info>

        <Actions width={width}>
          <ConditionalDisplay displayWhen={[!isGs]}>
            <ActionLink width={width} first to={`/${l('ROUTE_ORGS')}/${url_name}`}>
              <LinkButton wide>View</LinkButton>
            </ActionLink>
          </ConditionalDisplay>

          <ConditionalDisplay displayWhen={[canBeClaimed]}>
            <ActionLink width={width} first={isGs} to={`/${l('ROUTE_ORGS')}/${url_name}/claim`}>
              <LinkButton wide>Claim {l('ORG')}</LinkButton>
            </ActionLink>
          </ConditionalDisplay>

          <ConditionalDisplay displayWhen={[canEdit]}>
            <ActionLink width={width} first={isGs} to={`/dashboard/${l('ROUTE_ORGS')}/${id}`}>
              <LinkButton wide>Manage</LinkButton>
            </ActionLink>
          </ConditionalDisplay>

          <ConditionalDisplay displayWhen={[!isGs]}>
            <ActionLink
              width={width}
              last
              to={`/dashboard/${l('ROUTE_OPPS')}/new?beneficiary_id=${id}`}
            >
              <SmallButtonPrimary wide>Create {l('OPP')}</SmallButtonPrimary>
            </ActionLink>
          </ConditionalDisplay>
        </Actions>
      </ActionsWrapper>
    </SingleCard>
  )
}

export default OrganisationCard
